import { accountingType } from '@/features/master-table/models';
import {
  actionOfReflectionStatus,
  inspectionByExternalExpertReason,
  opinionByAdministrativeReviewTeam,
  skipAdministrativeReformTeamEvaluationReason,
} from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import { reviewTypeEnum } from '@/utils/enum';
import { z } from 'zod';
import { projectDetailUrlTypeEnum } from './report';

/// ////////////////////////////////////////
/// Enum
/// ////////////////////////////////////////

export const reportFormatType = z.enum([
  'format1',
  'format2',
  'format3',
  'format4',
  'format5',
]);

export const ReportFormatLabelModifier = {
  format1: t('report-format1'),
  format2: t('report-format2'),
  format3: t('report-format3'),
  format4: t('report-format4'),
  format5: t('report-format5'),
} as const satisfies Record<z.infer<typeof reportFormatType>, string>;

export const ExportReportFormatLabelModifier = {
  format1: t('format1'),
  format2: t('format2'),
  format3: t('format3'),
  format4: t('format4'),
  format5: t('format5'),
} as const satisfies Record<z.infer<typeof reportFormatType>, string>;

export const format1ColumnType = z.enum([
  'policyLevel1',
  'policyLevel2',
  'projectNumber',
  'name',
  'parentOrganization',
  'startYear',
  'endYear',
  'accountTitle',
  'executableAmount',
  'executionAmount',
  'initialBudgetTotal',
  'posteriorRequestedAmountTotal',
  'differencePosteriorRequestedSelfInitialBudget',
  'externalOpinion',
  'adminReformReview',
  'reflectedAmount',
  'reflectedContents',
  'externalEvaluationScope',
  'externalEvaluationReason',
  'externalEvaluationLastExecutionYear',
  'projectDetailPageUrl',
]);

export const format2ColumnType = z.enum([
  'policyLevel1',
  'policyLevel2',
  'projectNumber',
  'name',
  'adminReformReviewOpinion',
  'posteriorRequestedAmountTotal',
  'organization',
  'accountingType',
  'projectDetailPageUrl',
]);

export const format3ColumnType = z.enum([
  'projectNumber',
  'name',
  'previousFinalizedAmountTotal',
  'previousExecutionAmountTotal',
  'resultOfPublishing',
  'finalizedAmount',
  'requestedAmount',
  'differenceBetweenRequestedAmountAndInitialBudget',
  'accountingTypeName',
  'reflectedAmount',
  'reflectionStatusAction',
  'reflectionStatusContext',
  'projectDetailPageUrl',
]);

export const format4ColumnType = z.enum([
  'jurisdiction',
  'generalSpecialPreviousYearProjectCount',
  'generalSpecialAbolitionProjectCount',
  'generalSpecialAbolitionProjectReflectedAmount',
  'generalSpecialReductionProjectCount',
  'generalSpecialReductionProjectReflectedAmount',
  'generalSpecialImprovementProjectCount',
  'generalPreviousYearProjectCount',
  'generalAbolitionProjectCount',
  'generalAbolitionProjectReflectedAmount',
  'generalReductionProjectCount',
  'generalReductionProjectReflectedAmount',
  'generalAbolitionReductionTotalProjectCount',
  'generalAbolitionReductionTotalProjectReflectedAmount',
  'generalImprovementProjectCount',
  'generalProjectRequestedAmount',
  'specialPreviousYearProjectCount',
  'specialAbolitionProjectCount',
  'specialAbolitionProjectReflectedAmount',
  'specialReductionProjectCount',
  'specialReductionProjectReflectedAmount',
  'specialAbolitionReductionTotalProjectCount',
  'specialAbolitionReductionTotalProjectReflectedAmount',
  'specialImprovementProjectCount',
  'specialProjectRequestedAmount',
]);

export const format5ColumnType = z.enum([
  'policyLevel1',
  'policyLevel2',
  'projectNumber',
  'name',
  'parentOrganization',
  'startYear',
  'endYear',
  'accountTitle',
  'majorExpenditureBudgetPurposeParent',
  'majorExpenditureBudgetPurpose',
  'executableAmount',
  'executionAmount',
  'initialBudgetTotal',
  'skipAdministrativeReformTeamEvaluationReason',
  'projectDetailPageUrl',
]);

/// ////////////////////////////////////////
/// Model
/// ////////////////////////////////////////

export const ReportOrganizingTableOrganizationsModel = z.object({
  organizations: z.object({ id: z.string().uuid(), name: z.string() }).array(),
});

const ReportOrganizingTableFormat1ProjectDataModel = z.object({
  id: z.string(),
  name: z.string(),
  projectNumber: z.string(),
  startYear: z.number().nullable(),
  endYear: z.number().nullable(),
  previousFinalizedAmountTotal: z.number().nullable(), // 予算額計
  rawPreviousFinalizedAmountTotal: z.number().nullable(), // 予算額計（端数処理前）
  negativePreviousFinalizedAmountCount: z.number().nullable(), // 前年度予算額計のマイナス件数
  previousExecutionAmountTotal: z.number().nullable(), //  執行額
  rawPreviousExecutionAmountTotal: z.number().nullable(), //  執行額（端数処理前）
  negativePreviousYearExecutionAmountCount: z.number().nullable(), // 前年度執行額のマイナス件数
  initialBudgetTotal: z.number().nullable(), // 当年度当初予算額
  rawInitialBudgetTotal: z.number().nullable(), // 当年度当初予算額（端数処理前）
  negativeInitialBudgetCount: z.number().nullable(), // 当年度当初予算額のマイナス件数
  posteriorRequestedAmountTotal: z.number().nullable(), // 翌年度要求額
  rawPosteriorRequestedAmountTotal: z.number().nullable(), // 翌年度要求額（端数処理前）
  negativePosteriorRequestedAmountCount: z.number().nullable(), // 翌年度要求額のマイナス件数
  differencePosteriorRequestedSelfInitialBudget: z.number().nullable(), // 差引き
  rawDifferencePosteriorRequestedSelfInitialBudget: z.number().nullable(), // 差引き（端数処理前）
  externalOpinion: z.string().nullable(),
  adminReformReview: z.object({
    opinion: opinionByAdministrativeReviewTeam.nullable(), // 行政事業レビュー推進チームの所見
  }),
  reflectionStatus: z.object({
    reflectedAmount: z.number().nullable(), // 反映額
    rawReflectedAmount: z.number().nullable(), // 反映額（端数処理前）
    reflectionContent: z.object({
      action: actionOfReflectionStatus.nullable(),
    }),
  }),
  parentOrganization: z.string(),
  externalEvaluationScope: reviewTypeEnum.nullable(),
  externalEvaluationReason: inspectionByExternalExpertReason.nullable(),
  externalEvaluationLastExecutionYear: z.number().nullable(),
  accountTitle: z.string().nullable(),
  accountingType,
});

export const ReportOrganizingTableFormat1PolicesModel = z.object({
  policyName: z.string().nullable(),
  policyCode: z.number().nullable(),
  policyMeasuresData: z
    .object({
      measureName: z.string().nullable(),
      measureCode: z.number().nullable(),
      projectsData: ReportOrganizingTableFormat1ProjectDataModel.array(),
    })
    .array(),
});

export const ReportOrganizingTableFormat1SummaryModel = z.object({
  accountTitle: z.string().nullable(),
  accountingType,
  skipAdministrativeReformTeamEvaluationPreviousReflectionStatusReflectedAmount:
    z.number().nullable(),
  skipAdministrativeReformTeamEvaluationPreviousReflectionStatusRawReflectedAmount:
    z.number().nullable(),
  skipAdministrativeReformTeamEvaluationPreviousExecutionAmountTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationRawPreviousExecutionAmountTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationInitialBudgetTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationRawInitialBudgetTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationPosteriorRequestedAmountTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationRawPosteriorRequestedAmountTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationDifferencePosteriorRequestedSelfInitialBudget:
    z.number().nullable(),
  skipAdministrativeReformTeamEvaluationRawDifferencePosteriorRequestedSelfInitialBudget:
    z.number().nullable(),
  skipAdministrativeReformTeamEvaluationPreviousFinalizedAmountTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationRawPreviousFinalizedAmountTotal: z
    .number()
    .nullable(),
  administrativeReformTeamEvaluationPreviousReflectionStatusReflectedAmount: z
    .number()
    .nullable(),
  administrativeReformTeamEvaluationPreviousReflectionStatusRawReflectedAmount:
    z.number().nullable(),
  administrativeReformTeamEvaluationPreviousExecutionAmountTotal: z
    .number()
    .nullable(),
  administrativeReformTeamEvaluationRawPreviousExecutionAmountTotal: z
    .number()
    .nullable(),
  administrativeReformTeamEvaluationInitialBudgetTotal: z.number().nullable(),
  administrativeReformTeamEvaluationRawInitialBudgetTotal: z
    .number()
    .nullable(),
  administrativeReformTeamEvaluationPosteriorRequestedAmountTotal: z
    .number()
    .nullable(),
  administrativeReformTeamEvaluationRawPosteriorRequestedAmountTotal: z
    .number()
    .nullable(),
  administrativeReformTeamEvaluationDifferencePosteriorRequestedSelfInitialBudget:
    z.number().nullable(),
  administrativeReformTeamEvaluationRawDifferencePosteriorRequestedSelfInitialBudget:
    z.number().nullable(),
  administrativeReformTeamEvaluationPreviousFinalizedAmountTotal: z
    .number()
    .nullable(),
  administrativeReformTeamEvaluationRawPreviousFinalizedAmountTotal: z
    .number()
    .nullable(),
  comprehensivePreviousExecutionAmountTotal: z.number().nullable(),
  comprehensiveRawPreviousExecutionAmountTotal: z.number().nullable(),
  comprehensivePreviousReflectionStatusReflectedAmount: z.number().nullable(),
  comprehensivePreviousReflectionStatusRawReflectedAmount: z
    .number()
    .nullable(),
  comprehensivePreviousInitialBudgetTotal: z.number().nullable(),
  comprehensiveRawPreviousInitialBudgetTotal: z.number().nullable(),
  comprehensivePosteriorRequestedAmountTotal: z.number().nullable(),
  comprehensiveRawPosteriorRequestedAmountTotal: z.number().nullable(),
  comprehensiveDifferencePosteriorRequestedSelfInitialBudget: z
    .number()
    .nullable(),
  comprehensiveRawDifferencePosteriorRequestedSelfInitialBudget: z
    .number()
    .nullable(),
  comprehensivePreviousFinalizedAmountTotal: z.number().nullable(),
  comprehensiveRawPreviousFinalizedAmountTotal: z.number().nullable(),
});

export const ReportOrganizingTableFormat1Model =
  ReportOrganizingTableOrganizationsModel.extend({
    policies: ReportOrganizingTableFormat1PolicesModel.array(),
    summary: z.object({
      general: ReportOrganizingTableFormat1SummaryModel.array(),
      special: ReportOrganizingTableFormat1SummaryModel.array(),
    }),
  });

const Format2ProjectDataModel = z.object({
  id: z.string(),
  name: z.string().nullable(),
  projectNumber: z.string().nullable(),
  adminReformReviewOpinion: opinionByAdministrativeReviewTeam.nullable(),
  posteriorRequestedAmountTotal: z.number().nullable(),
  rawPosteriorRequestedAmountTotal: z.number().nullable(),
  negativeRequestedAmountCount: z.number().nullable(),
  organization: z.string().nullable(),
  accountingType: accountingType.nullable(),
  accountTitle: z.string().nullable(),
});

export const ReportOrganizingTableFormat2PolicesModel = z.object({
  policyName: z.string().nullable(),
  policyCode: z.number().nullable(),
  policyMeasuresData: z
    .object({
      measureName: z.string().nullable(),
      measureCode: z.number().nullable(),
      projectsData: Format2ProjectDataModel.array(),
    })
    .array(),
});

export const ReportOrganizingTableFormat2Model =
  ReportOrganizingTableOrganizationsModel.extend({
    policies: ReportOrganizingTableFormat2PolicesModel.array(),
    summary: z.object({
      generalAccountingTypeTotal: z.number().nullable(),
      rawGeneralAccountingTypeTotal: z.number().nullable(),
      specialAccountingType: z
        .object({
          accountTitle: z.string(),
          total: z.number().nullable(),
          rawTotal: z.number().nullable(),
        })
        .array(),
    }),
  });

export const ReportOrganizingTableFormat3ProjectModel = z.object({
  id: z.string(),
  name: z.string(),
  projectNumber: z.string(),
  accountTitleId: z.string().uuid().nullable(),
  accountTitleName: z.string().nullable(), // 勘定科目
  resultOfPublishing: z.string().nullable(), // 公開プロセス結果概要
  finalizedAmount: z.number().nullable(), // 予算額
  rawFinalizedAmount: z.number().nullable(), // 予算額（端数処理前）
  negativeFinalizedAmountCount: z.number().nullable(),
  reflectedAmount: z.number().nullable(), // 反映額
  rawReflectedAmount: z.number().nullable(), // 反映額（端数処理前）
  negativeReflectedAmountCount: z.number().nullable(),
  requestedAmount: z.number().nullable(), // 要求額
  rawRequestedAmount: z.number().nullable(), // 要求額（端数処理前）
  negativeRequestedAmountCount: z.number().nullable(),
  accountingTypeId: z.string().uuid().nullable(),
  accountingTypeType: accountingType.nullable(),
  accountingTypeName: z.string().nullable(), // 会計区分
  reflectionStatusAction: actionOfReflectionStatus.nullable(),
  reflectionStatusContext: z.string().nullable(), // 詳細
  previousExecutionAmountTotal: z.number().nullable(), // 前年執行
  rawPreviousExecutionAmountTotal: z.number().nullable(), // 前年執行（端数処理前）
  negativePreviousExecutionAmountTotalCount: z.number().nullable(),
  previousFinalizedAmountTotal: z.number().nullable(), // 前年予算額
  rawPreviousFinalizedAmountTotal: z.number().nullable(), // 前年予算額（端数処理前）
  negativePreviousFinalizedAmountTotalCount: z.number().nullable(),
  differenceBetweenRequestedAmountAndInitialBudget: z.number().nullable(), // 当年度差引き
  rawDifferenceBetweenRequestedAmountAndInitialBudget: z.number().nullable(), // 当年度差引き（端数処理前）
});

export const ReportOrganizingTableFormat3Model =
  ReportOrganizingTableOrganizationsModel.extend({
    projects: ReportOrganizingTableFormat3ProjectModel.array(),
    summary: z.object({
      finalizedAmount: z.number().nullable(),
      rawFinalizedAmount: z.number().nullable(),
      reflectedAmount: z.number().nullable(),
      rawReflectedAmount: z.number().nullable(),
      requestedAmount: z.number().nullable(),
      rawRequestedAmount: z.number().nullable(),
      previousExecutionAmountTotal: z.number().nullable(),
      rawPreviousExecutionAmountTotal: z.number().nullable(),
      previousFinalizedAmountTotal: z.number().nullable(),
      rawPreviousFinalizedAmountTotal: z.number().nullable(),
      differenceBetweenRequestedAmountAndInitialBudget: z.number().nullable(),
      rawDifferenceBetweenRequestedAmountAndInitialBudget: z
        .number()
        .nullable(),
    }),
  });

export const ReportOrganizingTableFormat4SummaryDataModel = z.object({
  previousYearProjectCount: z.number(),
  abolitionProjectCount: z.number(),
  abolitionProjectReflectedAmount: z.number().nullable(),
  reductionProjectCount: z.number(),
  reductionProjectReflectedAmount: z.number().nullable(),
  abolitionReductionProjectCount: z.number(),
  abolitionReductionProjectReflectedAmount: z.number().nullable(),
  improvementProjectCount: z.number(),
  projectRequestedAmount: z.number().nullable(),
});

export const ReportOrganizingTableFormat4SummaryByAccountModel = z.object({
  generalSpecial: ReportOrganizingTableFormat4SummaryDataModel.omit({
    abolitionReductionProjectCount: true,
    abolitionReductionProjectReflectedAmount: true,
    projectRequestedAmount: true,
  }),
  general: ReportOrganizingTableFormat4SummaryDataModel,
  special: ReportOrganizingTableFormat4SummaryDataModel,
});

const ReportOrganizingTableFormat4OrganizationModel = z.object({
  id: z.string().uuid(),
  name: z.string(),
  summaryByAccount: ReportOrganizingTableFormat4SummaryByAccountModel,
});

export const ReportOrganizingTableFormat4Model = z.object({
  organizations: ReportOrganizingTableFormat4OrganizationModel.array(),
  totalSummaryByAccount: ReportOrganizingTableFormat4SummaryByAccountModel,
});

const ReportOrganizingTableFormat5ProjectDataModel = z.object({
  id: z.string(),
  projectNumber: z.string(),
  name: z.string(),
  parentOrganization: z.string(),
  startYear: z.number().nullable(),
  endYear: z.number().nullable(),
  accountTitle: z.string().nullable(),
  accountingType,
  majorExpenditureBudgetPurposeParent: z.string().nullish(),
  majorExpenditureBudgetPurpose: z.string().nullish(),
  previousFinalizedAmountTotal: z.number().nullable(),
  rawPreviousFinalizedAmountTotal: z.number().nullable(), // 前年度予算額計（端数処理前）
  negativePreviousFinalizedAmountCount: z.number().nullable(), // 前年度予算額計のマイナス件数
  previousExecutionAmountTotal: z.number().nullable(),
  rawPreviousExecutionAmountTotal: z.number().nullable(), // 前年度執行額（端数処理前）
  negativePreviousYearExecutionAmountCount: z.number().nullable(), // 前年度執行額のマイナス件数
  initialBudgetTotal: z.number().nullable(),
  rawInitialBudgetTotal: z.number().nullable(), // 当年度当初予算額（端数処理前）
  negativeInitialBudgetCount: z.number().nullable(), // 当年度当初予算額のマイナス件数
  skipAdministrativeReformTeamEvaluationReason,
});

export const ReportOrganizingTableFormat5PolicesModel = z.object({
  policyName: z.string().nullable(),
  policyCode: z.number().nullable(),
  policyMeasuresData: z
    .object({
      measureName: z.string().nullable(),
      measureCode: z.number().nullable(),
      projectsData: ReportOrganizingTableFormat5ProjectDataModel.array(),
    })
    .array(),
});

export const ReportOrganizingTableFormat5SummaryModel = z.object({
  accountTitle: z.string().nullable(),
  accountingType,
  skipAdministrativeReformTeamEvaluationInitialBudgetTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationRawInitialBudgetTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationPreviousExecutionAmountTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationRawPreviousExecutionAmountTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationPreviousFinalizedAmountTotal: z
    .number()
    .nullable(),
  skipAdministrativeReformTeamEvaluationRawPreviousFinalizedAmountTotal: z
    .number()
    .nullable(),
});

export const ReportOrganizingTableFormat5Model = z.object({
  organizations: z.object({ id: z.string().uuid(), name: z.string() }).array(),
  policies: ReportOrganizingTableFormat5PolicesModel.array(),
  summary: z.object({
    general: ReportOrganizingTableFormat5SummaryModel.array(),
    special: ReportOrganizingTableFormat5SummaryModel.array(),
  }),
});

// /// ////////////////////////////////////////
// /// Schema
// /// ////////////////////////////////////////

export const ReportOrganizingTableFilterSchema = z.object({
  fiscalYear: z.any().transform((v) => Number(v)),
  organizationIds: z.string().uuid().array().min(1),
  formatNumber: reportFormatType,
  projectDetailUrlType: projectDetailUrlTypeEnum.nullish(),
});
